<template>
    <div class="flex flex-wrap items-center gap-3 px-6 py-3 border-b border-gray-200 bg-gray-50">
        <!-- Search -->
        <div class="relative flex-1" style="min-width: 200px; max-width: 320px;">
            <i class="far fa-search absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-xs pointer-events-none"></i>
            <input
                type="text"
                :value="filters.search"
                @input="onSearchInput"
                class="block w-full rounded-md border-gray-300 pl-9 pr-8 py-1.5 text-sm shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                placeholder="Zoeken op bestandsnaam..."
            />
            <button
                v-if="filters.search"
                class="absolute right-2 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center rounded-full text-gray-400 hover:text-gray-600 hover:bg-gray-100"
                @click="emit('search', '')"
            >
                <i class="far fa-times text-xs"></i>
            </button>
        </div>

        <!-- Mobile: collapse the filters behind a toggle -->
        <button
            type="button"
            class="lg:hidden inline-flex items-center px-3 py-1.5 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
            @click="showFilters = !showFilters"
        >
            <i class="far fa-sliders-h mr-1.5 text-gray-400"></i> Filters
            <span v-if="activeFilterCount" class="ml-1.5 inline-flex items-center justify-center w-5 h-5 rounded-full bg-indigo-100 text-indigo-700 text-xs">{{ activeFilterCount }}</span>
        </button>

        <!-- Filter controls: inline on lg, collapsible on mobile -->
        <div class="w-full lg:w-auto gap-3" :class="showFilters ? 'flex flex-wrap items-center' : 'hidden lg:flex lg:items-center'">
        <!-- Type filter -->
        <select :value="filters.type" @change="emit('type', $event.target.value)" class="block rounded-md border-gray-300 py-1.5 text-sm shadow-sm focus:border-indigo-500 focus:ring-indigo-500">
            <option value="">Alle types</option>
            <option value="image">Afbeeldingen</option>
            <option value="video">Video</option>
            <option value="audio">Audio</option>
            <option value="document">Documenten</option>
            <option value="archive">Archieven</option>
            <option value="other">Overig</option>
        </select>

        <!-- Source filter -->
        <select :value="filters.source" @change="emit('source', $event.target.value)" class="block rounded-md border-gray-300 py-1.5 text-sm shadow-sm focus:border-indigo-500 focus:ring-indigo-500">
            <option value="">Alle bronnen</option>
            <option value="spatie">Contentmedia</option>
            <option value="lfm">Uploads</option>
        </select>

        <!-- Usage filter -->
        <select :value="filters.usage" @change="emit('usage', $event.target.value)" class="block rounded-md border-gray-300 py-1.5 text-sm shadow-sm focus:border-indigo-500 focus:ring-indigo-500">
            <option value="">Alle bestanden</option>
            <option value="used">In gebruik</option>
            <option value="unused">Niet in gebruik</option>
        </select>

        <!-- Used in module (cascades to a specific record) -->
        <select v-if="modules.length" :value="filters.module" @change="emit('module', $event.target.value)" class="block rounded-md border-gray-300 py-1.5 text-sm shadow-sm focus:border-indigo-500 focus:ring-indigo-500">
            <option value="">In elke module</option>
            <option v-for="m in modules" :key="m.value" :value="m.value">{{ m.label }}</option>
        </select>

        <!-- Which record of the chosen module -->
        <select v-if="filters.module && records.length" :value="filters.module_id" @change="emit('module_id', $event.target.value)" class="block rounded-md border-gray-300 py-1.5 text-sm shadow-sm focus:border-indigo-500 focus:ring-indigo-500" style="max-width:200px;">
            <option value="">Alle</option>
            <option v-for="r in records" :key="r.value" :value="r.value">{{ r.label }}</option>
        </select>

        <!-- Sort: field + direction -->
        <div class="flex items-center gap-1">
            <select :value="sortField" @change="setSortField($event.target.value)" class="block rounded-md border-gray-300 py-1.5 text-sm shadow-sm focus:border-indigo-500 focus:ring-indigo-500">
                <option value="date">Datum</option>
                <option value="name">Naam</option>
                <option value="size">Grootte</option>
            </select>
            <button
                type="button"
                @click="toggleSortDir"
                :title="sortAsc ? 'Oplopend (A-Z / oud-nieuw / klein-groot)' : 'Aflopend (Z-A / nieuw-oud / groot-klein)'"
                class="inline-flex items-center justify-center w-8 h-8 rounded-md border border-gray-300 bg-white text-gray-500 hover:bg-gray-50 focus:outline-none focus:ring-1 focus:ring-indigo-500"
            >
                <i :class="sortAsc ? 'far fa-arrow-up' : 'far fa-arrow-down'"></i>
            </button>
        </div>
        </div>
        <!-- /Filter controls -->

        <!-- View toggle -->
        <div class="flex rounded-md shadow-sm ml-auto">
            <button
                class="relative inline-flex items-center px-2.5 py-1.5 rounded-l-md border text-sm font-medium focus:z-10 focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
                :class="viewMode === 'grid' ? 'bg-indigo-500 border-indigo-500 text-white' : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'"
                @click="$emit('view-change', 'grid')"
                title="Rasterweergave"
            >
                <i class="far fa-th"></i>
            </button>
            <button
                class="relative -ml-px inline-flex items-center px-2.5 py-1.5 rounded-r-md border text-sm font-medium focus:z-10 focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
                :class="viewMode === 'list' ? 'bg-indigo-500 border-indigo-500 text-white' : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'"
                @click="$emit('view-change', 'list')"
                title="Lijstweergave"
            >
                <i class="far fa-list"></i>
            </button>
        </div>
    </div>
</template>

<script>
export default {
    name: 'MediaFilters',

    props: {
        filters: { type: Object, required: true },
        viewMode: { type: String, required: true },
        modules: { type: Array, default: function () { return []; } },
        records: { type: Array, default: function () { return []; } },
    },

    data() {
        return {
            searchTimeout: null,
            showFilters: false,
        };
    },

    computed: {
        // Number of active (non-default) filters — shown on the mobile toggle.
        activeFilterCount() {
            var f = this.filters;
            var n = 0;
            ['type', 'source', 'usage', 'module', 'module_id'].forEach(function (k) {
                if (f[k]) n++;
            });
            if (f.sort && f.sort !== 'newest') n++;
            return n;
        },

        sortField() {
            var s = this.filters.sort || 'newest';
            if (s.indexOf('name') === 0) return 'name';
            if (s.indexOf('size') === 0) return 'size';
            return 'date';
        },
        sortAsc() {
            return ['oldest', 'name_asc', 'size_asc'].indexOf(this.filters.sort) !== -1;
        },
    },

    methods: {
        composeSort(field, asc) {
            if (field === 'name') return asc ? 'name_asc' : 'name_desc';
            if (field === 'size') return asc ? 'size_asc' : 'size_desc';
            return asc ? 'oldest' : 'newest'; // date
        },

        setSortField(field) {
            this.emit('sort', this.composeSort(field, this.sortAsc));
        },

        toggleSortDir() {
            this.emit('sort', this.composeSort(this.sortField, !this.sortAsc));
        },

        onSearchInput(e) {
            var self = this;
            var value = e.target.value;

            clearTimeout(this.searchTimeout);
            this.searchTimeout = setTimeout(function () {
                self.emit('search', value);
            }, 300);
        },

        emit(key, value) {
            var update = {};
            update[key] = value;
            this.$emit('filter-change', update);
        },
    },
};
</script>
